import type { CSSProperties, JSX } from 'react';

/**
 * Flat geometric furniture drawn from the design tokens, one per category.
 *
 * These replaced supplier photography on the homepage. Photography of a *category* is a
 * problem no ranking fully solves: the shot has to stand for hundreds of products, and the
 * best available image is still one product in one colour — three attempts at ranking it
 * produced dimension arrows, then detail crops, then good lifestyle shots that still
 * advertise one sofa as if it were a category. A drawing says "sofas" without claiming any
 * particular sofa, and owes nothing to a supplier's photographer.
 *
 * One viewBox, one baseline, one lighting direction across all sixteen, so the grid reads
 * as a set rather than sixteen unrelated drawings.
 */
const VIEW = '0 0 200 132';
const FLOOR = 108;

/**
 * Every shape references a role, never a colour, and the role is bound to a material
 * family on the root. That is what lets a wooden chest and an upholstered sofa share one
 * set of drawing code and still look made of different things.
 */
const MID = 'var(--g-mid)';
const DEEP = 'var(--g-deep)';
const LIFT = 'var(--g-lift)';
const METAL = 'var(--color-glyph-metal)';
const TRIM = 'var(--g-trim)';

const MATERIALS = {
  upholstery: {
    '--g-deep': 'var(--color-uph-deep)',
    '--g-mid': 'var(--color-uph)',
    '--g-lift': 'var(--color-uph-lift)',
    '--g-trim': 'var(--color-wood)',
  },
  wood: {
    '--g-deep': 'var(--color-wood-deep)',
    '--g-mid': 'var(--color-wood)',
    '--g-lift': 'var(--color-wood-lift)',
    '--g-trim': 'var(--color-uph)',
  },
} as const;

/** Which family a category is made of. Upholstery unless the thing is mostly timber. */
const MATERIAL: Record<string, keyof typeof MATERIALS> = {
  szafki: 'wood',
  biurka: 'wood',
  krzesla: 'wood',
  dodatki: 'wood',
};

/** Roughly the footprint of each piece, so the shadow is not wider than what casts it. */
const SHADOW_RX: Record<string, number> = {
  kanapy: 62, sofy: 58, narozniki: 66, siedziska: 68, lozka: 62, sypialnia: 58,
  materace: 60, biurka: 56, szafki: 46, 'system-modulowy': 66,
  fotele: 36, krzesla: 26, pufy: 42, podnozki: 36, poduszki: 48, dodatki: 40,
};

function Legs({ xs, y = FLOOR, h = 14 }: { xs: number[]; y?: number; h?: number }): JSX.Element {
  return (
    <>
      {xs.map((x) => (
        <rect key={x} x={x} y={y} width="6" height={h} rx="3" fill={METAL} />
      ))}
    </>
  );
}

/** The lit top edge. One direction for all sixteen, or they stop being a family. */
function Highlight({ x, y, w, h = 5 }: { x: number; y: number; w: number; h?: number }): JSX.Element {
  return <rect x={x} y={y} width={w} height={h} rx={h / 2} fill={LIFT} opacity="0.55" />;
}

const GLYPHS: Record<string, JSX.Element> = {
  kanapy: (
    <>
      <rect x="26" y="62" width="148" height="46" rx="8" fill={MID} />
      <rect x="26" y="46" width="148" height="26" rx="8" fill={DEEP} />
      <Highlight x={34} y={48} w={132} />
      <rect x="20" y="52" width="18" height="52" rx="8" fill={DEEP} />
      <rect x="162" y="52" width="18" height="52" rx="8" fill={DEEP} />
      <rect x="62" y="52" width="34" height="18" rx="5" fill={TRIM} />
      <Legs xs={[36, 158]} />
    </>
  ),
  sofy: (
    <>
      <rect x="34" y="64" width="132" height="44" rx="8" fill={MID} />
      <rect x="34" y="48" width="132" height="24" rx="8" fill={DEEP} />
      <Highlight x={42} y={50} w={116} />
      <rect x="28" y="54" width="16" height="50" rx="8" fill={DEEP} />
      <rect x="156" y="54" width="16" height="50" rx="8" fill={DEEP} />
      <Legs xs={[42, 152]} />
    </>
  ),
  narozniki: (
    <>
      <rect x="22" y="62" width="96" height="46" rx="8" fill={MID} />
      <rect x="112" y="76" width="66" height="32" rx="8" fill={LIFT} />
      <rect x="22" y="44" width="96" height="26" rx="8" fill={DEEP} />
      <Highlight x={30} y={46} w={80} />
      <rect x="16" y="50" width="16" height="54" rx="8" fill={DEEP} />
      <rect x="52" y="50" width="30" height="16" rx="5" fill={TRIM} />
      <Legs xs={[30, 104, 166]} />
    </>
  ),
  siedziska: (
    <>
      <rect x="18" y="66" width="92" height="42" rx="8" fill={MID} />
      <rect x="18" y="48" width="92" height="26" rx="8" fill={DEEP} />
      <Highlight x={26} y={50} w={76} />
      <rect x="12" y="54" width="14" height="50" rx="7" fill={DEEP} />
      <rect x="102" y="54" width="14" height="50" rx="7" fill={DEEP} />
      <rect x="44" y="54" width="26" height="14" rx="5" fill={TRIM} />
      <Legs xs={[24, 98]} />
      <ellipse cx="156" cy="80" rx="28" ry="9" fill={DEEP} />
      <rect x="128" y="80" width="56" height="22" rx="9" fill={LIFT} />
      <Legs xs={[134, 172]} y={100} h={9} />
    </>
  ),
  fotele: (
    <>
      <rect x="66" y="66" width="70" height="42" rx="8" fill={MID} />
      <rect x="70" y="38" width="62" height="34" rx="9" fill={DEEP} />
      <Highlight x={78} y={40} w={46} />
      <rect x="58" y="60" width="14" height="42" rx="7" fill={DEEP} />
      <rect x="130" y="60" width="14" height="42" rx="7" fill={DEEP} />
      <rect x="84" y="46" width="26" height="14" rx="5" fill={TRIM} />
      <Legs xs={[70, 126]} />
    </>
  ),
  krzesla: (
    <>
      <rect x="74" y="72" width="56" height="12" rx="5" fill={MID} />
      <rect x="76" y="36" width="52" height="10" rx="5" fill={DEEP} />
      <rect x="78" y="46" width="8" height="28" rx="4" fill={DEEP} />
      <rect x="118" y="46" width="8" height="28" rx="4" fill={DEEP} />
      <Highlight x={78} y={72} w={48} h={4} />
      <Legs xs={[78, 118]} y={82} h={26} />
    </>
  ),
  pufy: (
    <>
      <ellipse cx="100" cy="72" rx="46" ry="14" fill={LIFT} />
      <rect x="54" y="72" width="92" height="30" rx="10" fill={MID} />
      <Legs xs={[64, 130]} y={100} h={10} />
    </>
  ),
  podnozki: (
    <>
      <rect x="62" y="76" width="76" height="24" rx="8" fill={MID} />
      <rect x="62" y="70" width="76" height="12" rx="6" fill={LIFT} />
      <Legs xs={[70, 124]} y={98} h={12} />
    </>
  ),
  lozka: (
    <>
      <rect x="24" y="34" width="20" height="62" rx="7" fill={DEEP} />
      <rect x="40" y="70" width="138" height="26" rx="7" fill={MID} />
      <rect x="40" y="62" width="138" height="12" rx="6" fill={LIFT} />
      <rect x="54" y="50" width="34" height="16" rx="5" fill={TRIM} />
      <Legs xs={[48, 164]} y={94} h={14} />
    </>
  ),
  sypialnia: (
    <>
      <rect x="28" y="38" width="18" height="58" rx="7" fill={DEEP} />
      <rect x="42" y="72" width="112" height="24" rx="7" fill={MID} />
      <rect x="42" y="64" width="112" height="12" rx="6" fill={LIFT} />
      <rect x="160" y="60" width="20" height="36" rx="4" fill={DEEP} />
      <circle cx="170" cy="48" r="9" fill={TRIM} />
      <Legs xs={[50, 140]} y={94} h={14} />
    </>
  ),
  materace: (
    <>
      <rect x="30" y="72" width="140" height="16" rx="8" fill={MID} />
      <rect x="30" y="60" width="140" height="14" rx="7" fill={LIFT} />
      <rect x="30" y="86" width="140" height="12" rx="6" fill={DEEP} />
      <Legs xs={[42, 152]} y={96} h={12} />
    </>
  ),
  poduszki: (
    <>
      <rect x="42" y="52" width="60" height="56" rx="12" fill={DEEP} transform="rotate(-8 72 80)" />
      <rect x="94" y="62" width="56" height="48" rx="12" fill={MID} transform="rotate(6 122 86)" />
      <rect x="106" y="74" width="22" height="10" rx="5" fill={TRIM} transform="rotate(6 117 79)" />
    </>
  ),
  biurka: (
    <>
      <rect x="26" y="58" width="148" height="12" rx="6" fill={MID} />
      <Highlight x={32} y={59} w={136} h={4} />
      <rect x="38" y="70" width="124" height="10" rx="4" fill={DEEP} />
      <Legs xs={[44, 150]} y={78} h={30} />
      <rect x="72" y="82" width="56" height="6" rx="3" fill={TRIM} opacity="0.8" />
    </>
  ),
  szafki: (
    <>
      <rect x="46" y="40" width="108" height="66" rx="7" fill={MID} />
      <Highlight x={52} y={41} w={96} h={4} />
      <rect x="54" y="54" width="92" height="20" rx="4" fill={DEEP} />
      <rect x="54" y="80" width="92" height="20" rx="4" fill={DEEP} />
      <circle cx="100" cy="64" r="4" fill={TRIM} />
      <circle cx="100" cy="90" r="4" fill={TRIM} />
      <Legs xs={[54, 140]} y={104} h={12} />
    </>
  ),
  'system-modulowy': (
    <>
      <rect x="24" y="70" width="52" height="38" rx="7" fill={MID} />
      <rect x="82" y="70" width="52" height="38" rx="7" fill={DEEP} />
      <rect x="140" y="70" width="40" height="38" rx="7" fill={LIFT} />
      <rect x="82" y="48" width="52" height="18" rx="6" fill={TRIM} opacity="0.85" />
      <Legs xs={[32, 154]} />
    </>
  ),
  // A lamp and a vessel: the things that are not the furniture. Two objects, not three —
  // a stack of books alongside them overlapped the vase into a single unreadable shape,
  // and the tile has to work at 140px wide.
  dodatki: (
    <>
      <path d="M64 54 L92 54 L104 78 L52 78 Z" fill={DEEP} />
      <rect x="75" y="78" width="6" height="26" rx="3" fill={METAL} />
      <rect x="64" y="102" width="28" height="6" rx="3" fill={METAL} />
      <rect x="134" y="64" width="10" height="14" rx="3" fill={LIFT} />
      <path d="M139 78 q17 0 17 15 t-17 15 q-17 0-17-15 t17-15Z" fill={MID} />
    </>
  ),
};

const FALLBACK = (
  <>
    <rect x="40" y="60" width="120" height="48" rx="8" fill={MID} />
    <rect x="40" y="48" width="120" height="18" rx="7" fill={DEEP} />
    <Highlight x={48} y={50} w={104} />
    <Legs xs={[48, 146]} />
  </>
);

export function CategoryGlyph({ slug, className }: { slug: string; className?: string }): JSX.Element {
  const material = MATERIALS[MATERIAL[slug] ?? 'upholstery'];
  return (
    <svg
      viewBox={VIEW}
      className={className}
      role="presentation"
      aria-hidden="true"
      preserveAspectRatio="xMidYMax meet"
      style={material as CSSProperties}
    >
      {/*
        The piece lifts and its shadow spreads, which is what reads as "off the ground"
        rather than "moved up the page" — a translate on its own just slides a sticker.
        The shadow is slower than the lift, so the two are not obviously one keyframe.
      */}
      <defs>
        {/* A flat ellipse reads as a grey oval sitting next to the furniture. The falloff
            is what makes it read as shadow. Id is per-slug because several of these
            render on one page and duplicate ids are not valid markup. */}
        <radialGradient id={`glyph-shadow-${slug}`}>
          <stop offset="0%" stopColor="var(--color-ink)" stopOpacity="0.20" />
          <stop offset="55%" stopColor="var(--color-ink)" stopOpacity="0.10" />
          <stop offset="100%" stopColor="var(--color-ink)" stopOpacity="0" />
        </radialGradient>
      </defs>
      <ellipse
        cx="100"
        cy={FLOOR + 16}
        rx={SHADOW_RX[slug] ?? 46}
        ry="7"
        fill={`url(#glyph-shadow-${slug})`}
        className="origin-center transition-all duration-[700ms] ease-[var(--ease-spring)] [transform-box:fill-box] group-hover:scale-x-[1.14] group-hover:opacity-75 motion-reduce:transition-none motion-reduce:group-hover:scale-x-100"
      />
      <g className="origin-bottom transition-transform duration-[520ms] ease-[var(--ease-spring)] [transform-box:fill-box] group-hover:-translate-y-[7px] motion-reduce:transition-none motion-reduce:group-hover:translate-y-0">
        {GLYPHS[slug] ?? FALLBACK}
      </g>
    </svg>
  );
}
